import {
	Box,
	CircularProgress,
	IconButton,
	Slider,
	Typography,
} from '@mui/material';
import React from 'react';
import Webcam from 'react-webcam';
import Lottie from 'lottie-react';
import tapAnimation from './tapAnimation.json';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useCamera } from '../../hooks/useCamera';

interface CameraComponentProps {
	setLicensePlateNumber: (val: string) => void;
	setSelectedState: (val: string) => void;
	setIsCameraOn: (val: boolean) => void;
	setCapturedImage: (val: string) => void;
	setIsLoading: (val: boolean) => void;
	onCleanScan?: (plate: string, state: string) => void;
}

const CameraComponent: React.FC<CameraComponentProps> = ({
	setCapturedImage,
	setIsLoading,
	setLicensePlateNumber,
	setSelectedState,
	setIsCameraOn,
	onCleanScan,
}) => {
	const {
		webcamRef,
		isWebcamLoaded,
		showScanAnimation,
		setIsWebcamLoaded,
		debouncedApplyZoom,
		maxZoom,
		minZoom,
		zoomLevel,
		setZoomLevel,
		capture,
		viewFinderBoxRef,
	} = useCamera(setCapturedImage, setIsCameraOn, setIsLoading, onCleanScan);
	return (
		<Box
			ref={viewFinderBoxRef}
			sx={{
				position: 'relative',
				width: '100%',
				maxWidth: '500px',
				aspectRatio: '2.35 / 1', // Automatically calculates height
				mt: 1,
			}}
		>
			{!isWebcamLoaded && (
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						bgcolor: 'rgba(0,0,0,0.3)',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						borderRadius: '8px',
						zIndex: 10,
					}}
				>
					<CircularProgress color="inherit" />
				</Box>
			)}

			{showScanAnimation && (
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						bgcolor: 'rgba(0,0,0,0.3)',
						borderRadius: '8px',
						zIndex: 10,
						pointerEvents: 'none',
					}}
				>
					<Lottie
						animationData={tapAnimation}
						loop
						style={{
							width: 120,
							height: 120,
						}}
					/>
				</Box>
			)}

			<Webcam
				audio={false}
				ref={webcamRef}
				screenshotFormat="image/jpeg"
				videoConstraints={{
					facingMode: 'environment',
				}}
				onUserMedia={() => {
					setIsWebcamLoaded(true);
					debouncedApplyZoom(zoomLevel);
				}}
				onClick={() => capture(setLicensePlateNumber, setSelectedState)}
				style={{
					width: '100%',
					height: '100%',
					objectFit: 'cover', // Crop the excess and fit into container
					borderRadius: '8px',
				}}
			/>

			{/* Overlay text message */}
			<Typography
				variant="caption"
				color="white"
				sx={{
					position: 'absolute',
					top: '0%', // Adjust as needed
					left: 0,
					width: '100%', // Ensures full width
					textAlign: 'center',
					backgroundColor: 'rgba(0, 0, 0, 0.5)', // Improves readability
					padding: '4px 0', // Ensure consistent padding
					whiteSpace: 'nowrap', // Prevents text from wrapping
					overflow: 'hidden', // Hides overflowing text
					textOverflow: 'ellipsis', // Adds "..." when text overflows
					borderRadius: '8px 8px 0 0', // Match the webcam's rounded edges
				}}
			>
				Fit the license plate within the dotted lines
			</Typography>

			<Box
				display="flex"
				flexDirection="row"
				alignItems="center"
				width="100%"
				gap={2}
			>
				{maxZoom > 1 ? (
					<>
						<IconButton
							onClick={() => {
								const newZoom = Math.max(
									zoomLevel - 0.5,
									minZoom,
								);
								setZoomLevel(newZoom);
								debouncedApplyZoom(newZoom);
							}}
						>
							<ZoomOutIcon />
						</IconButton>

						<Slider
							value={zoomLevel}
							min={minZoom}
							max={maxZoom}
							step={0.1}
							onChange={(e, newValue) => {
								setZoomLevel(newValue as number);
								debouncedApplyZoom(newValue as number);
							}}
							onChangeCommitted={(e, newValue) => {
								// Force the debounced function to execute immediately
								debouncedApplyZoom.flush();
							}}
							aria-labelledby="continuous-slider"
							sx={{ width: '100%' }}
						/>

						<IconButton
							onClick={() => {
								const newZoom = Math.min(
									zoomLevel + 0.5,
									maxZoom,
								);
								setZoomLevel(newZoom);
								debouncedApplyZoom(newZoom);
							}}
						>
							<ZoomInIcon />
						</IconButton>
					</>
				) : (
					<Typography
						variant="caption"
						color={'text.secondary'}
						width={'100%'}
						textAlign={'center'}
					>
						Zoom not supported on this device
					</Typography>
				)}
			</Box>

			{/* Overlay rectangle to guide user. This is the dotted line shown for license plate framing */}
			<Box
				sx={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					width: '90%',
					height: '75%',
					border: '4px dotted white',
					borderRadius: '4px',
					zIndex: 5,
					pointerEvents: 'none',
				}}
			/>
		</Box>
	);
};

export default CameraComponent;
